import { E_LEND_CHAIN_ID, getPerspectiveAddresses } from '@/config/euler-contracts'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerEarnGovernedPerspectiveAbi } from 'abis/evm/eulerEarnGovernedPerspective'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { zeroAddress } from 'viem'
import { useReadContracts } from 'wagmi'

export interface ELendVaultExposureSummary {
  borrowableCount: number
  collateralCount: number
}

export type ELendVaultExposureSummaryMap = Map<string, ELendVaultExposureSummary>

function addressKey(address: Address): string {
  return address.toLowerCase()
}

function isActiveCollateral(liquidationLTV: bigint, targetTimestamp: bigint): boolean {
  const currentTimestamp = Math.floor(Date.now() / 1000)
  return liquidationLTV > 0n || targetTimestamp > BigInt(currentTimestamp)
}

function isBorrowableCollateral(
  borrowLTV: bigint,
  liquidationLTV: bigint,
  targetTimestamp: bigint,
  totalBorrowed?: bigint,
): boolean {
  const currentTimestamp = Math.floor(Date.now() / 1000)
  return Boolean(
    (borrowLTV > 0n && liquidationLTV > 0n) ||
      targetTimestamp > BigInt(currentTimestamp) ||
      (totalBorrowed && totalBorrowed > 0n && liquidationLTV > 0n),
  )
}

interface BorrowablePair {
  vaultAddress: Address
  collateralAddress: Address
}

interface CollateralPair {
  collateralAddress: Address
  liabilityAddress: Address
}

export function useELendVaultExposureSummaries(vaultAddresses: Address[]): ELendVaultExposureSummaryMap {
  const targetVaults = useMemo(() => {
    const seen = new Set<string>()
    return vaultAddresses.filter((address) => {
      const key = addressKey(address)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [vaultAddresses])

  const targetVaultSet = useMemo(() => new Set(targetVaults.map(addressKey)), [targetVaults])
  const perspectives = getPerspectiveAddresses(E_LEND_CHAIN_ID)

  const perspectiveContracts = useMemo(() => {
    if (!perspectives) return []

    return [perspectives.escrowedCollateral, perspectives.governed, perspectives.evkFactory, perspectives.edgeFactory]
      .filter((address): address is Address => Boolean(address && address !== zeroAddress))
      .map((address) => ({
        address,
        abi: eulerEarnGovernedPerspectiveAbi,
        functionName: 'verifiedArray' as const,
        chainId: E_LEND_CHAIN_ID,
      }))
  }, [perspectives])

  const { data: perspectiveResults } = useReadContracts({
    contracts: perspectiveContracts,
    query: {
      enabled: perspectiveContracts.length > 0 && targetVaults.length > 0,
      staleTime: 60_000,
    },
  })

  const allVaults = useMemo(() => {
    if (!perspectiveResults) return []

    const vaultSet = new Set<Address>()
    for (const result of perspectiveResults) {
      if (result.status === 'success' && Array.isArray(result.result)) {
        for (const vault of result.result as Address[]) {
          vaultSet.add(vault)
        }
      }
    }
    return Array.from(vaultSet)
  }, [perspectiveResults])

  const ltvListContracts = useMemo(
    () =>
      allVaults.map((address) => ({
        address,
        abi: eVaultImplementationAbi,
        functionName: 'LTVList' as const,
        chainId: E_LEND_CHAIN_ID,
      })),
    [allVaults],
  )

  const { data: allLtvLists } = useReadContracts({
    contracts: ltvListContracts,
    query: {
      enabled: ltvListContracts.length > 0 && targetVaults.length > 0,
      staleTime: 60_000,
    },
  })

  const { borrowablePairs, collateralPairs } = useMemo(() => {
    const nextBorrowablePairs: BorrowablePair[] = []
    const nextCollateralPairs: CollateralPair[] = []

    if (!allLtvLists) {
      return { borrowablePairs: nextBorrowablePairs, collateralPairs: nextCollateralPairs }
    }

    allVaults.forEach((vaultAddress, index) => {
      const ltvList = allLtvLists[index]?.result as Address[] | undefined
      if (!ltvList) return

      if (targetVaultSet.has(addressKey(vaultAddress))) {
        for (const collateralAddress of ltvList) {
          nextBorrowablePairs.push({ vaultAddress, collateralAddress })
        }
      }

      for (const collateralAddress of ltvList) {
        if (targetVaultSet.has(addressKey(collateralAddress))) {
          nextCollateralPairs.push({ collateralAddress, liabilityAddress: vaultAddress })
        }
      }
    })

    return { borrowablePairs: nextBorrowablePairs, collateralPairs: nextCollateralPairs }
  }, [allLtvLists, allVaults, targetVaultSet])

  const borrowableContracts = useMemo(
    () =>
      borrowablePairs.map(({ vaultAddress, collateralAddress }) => ({
        address: vaultAddress,
        abi: eVaultImplementationAbi,
        functionName: 'LTVFull' as const,
        args: [collateralAddress] as const,
        chainId: E_LEND_CHAIN_ID,
      })),
    [borrowablePairs],
  )

  const collateralContracts = useMemo(
    () =>
      collateralPairs.flatMap(({ collateralAddress, liabilityAddress }) => [
        {
          address: liabilityAddress,
          abi: eVaultImplementationAbi,
          functionName: 'LTVFull' as const,
          args: [collateralAddress] as const,
          chainId: E_LEND_CHAIN_ID,
        },
        {
          address: liabilityAddress,
          abi: eVaultImplementationAbi,
          functionName: 'totalBorrows' as const,
          chainId: E_LEND_CHAIN_ID,
        },
      ]),
    [collateralPairs],
  )

  const { data: borrowableData } = useReadContracts({
    contracts: borrowableContracts,
    query: {
      enabled: borrowableContracts.length > 0,
      staleTime: 60_000,
    },
  })

  const { data: collateralData } = useReadContracts({
    contracts: collateralContracts,
    query: {
      enabled: collateralContracts.length > 0,
      staleTime: 60_000,
    },
  })

  return useMemo(() => {
    const summaries: ELendVaultExposureSummaryMap = new Map(
      targetVaults.map((address) => [addressKey(address), { borrowableCount: 0, collateralCount: 0 }]),
    )

    borrowablePairs.forEach(({ vaultAddress }, index) => {
      const ltvConfig = borrowableData?.[index]?.result as [bigint, bigint, bigint, bigint, number] | undefined
      if (!ltvConfig) return

      const [, liquidationLTV, , targetTimestamp] = ltvConfig
      if (!isActiveCollateral(liquidationLTV, targetTimestamp)) return

      const summary = summaries.get(addressKey(vaultAddress))
      if (summary) summary.borrowableCount += 1
    })

    collateralPairs.forEach(({ collateralAddress }, index) => {
      const baseIndex = index * 2
      const ltvConfig = collateralData?.[baseIndex]?.result as [bigint, bigint, bigint, bigint, number] | undefined
      const totalBorrowed = collateralData?.[baseIndex + 1]?.result as bigint | undefined
      if (!ltvConfig) return

      const [borrowLTV, liquidationLTV, , targetTimestamp] = ltvConfig
      if (!isBorrowableCollateral(borrowLTV, liquidationLTV, targetTimestamp, totalBorrowed)) return

      const summary = summaries.get(addressKey(collateralAddress))
      if (summary) summary.collateralCount += 1
    })

    return summaries
  }, [borrowableData, borrowablePairs, collateralData, collateralPairs, targetVaults])
}
